/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import FormGroup from "@mui/material/FormGroup";

import { Typography, IconButton, Button, Tooltip, Stack, CardContent, Alert } from "@mui/material";
import Box from "@mui/material/Box";

import EditIcon from "@mui/icons-material/Edit";

import { UserRoleItemToggle } from "./UserRoleItemToggle";
import ConfirmDeleteModal from "../../../components/confirmation/ConfirmDeleteModal";
import { UncontrolledEditableField } from "../../../components/form-controls/UncontrolledEditableField";
import { DetailedUser, Effect, Nullable, Setter } from "../../../utils/Interfaces";
import { deleteUser, disableUser, enableUser, refreshUsers, updateUserInfo } from "./userManagementFunctions";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import Card from "@mui/material/Card";
import { validateEmail } from "../../../utils/Functions";
import { useAppDispatch } from "../../../app/hooks";

export interface UserInfoPanelProps {
    user: Nullable<DetailedUser>;
    existingUsers: DetailedUser[];
    onRemoveUser: Setter<number>;
}

const UserInfoPanel = (props: UserInfoPanelProps) => {
    const dispatch = useAppDispatch();

    const { user, existingUsers, onRemoveUser } = props;

    const [editingGeneralInfo, setEditingGeneralInfo] = useState<boolean>(false);

    const [email, setEmail] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");

    const [hasOffice, setHasOffice] = useState<boolean>(false);
    const [hasTech, setHasTech] = useState<boolean>(false);
    const [hasAdmin, setHasAdmin] = useState<boolean>(false);
    const [hasMaterials, setHasMaterials] = useState<boolean>(false);
    const [hasProjects, setHasProjects] = useState<boolean>(false);

    const [deleteUserModalOpen, setDeleteUserModalOpen] = useState<boolean>(false);

    function closeDeleteUserModal() {
        setDeleteUserModalOpen(false);
    }

    function setDefaultUserGeneralInfo() {
        setEmail(user?.email as string);
        setFirstName(user?.first_name as string);
        setLastName(user?.last_name as string);
    }

    function emailTaken() {
        // Since the selected user is part of the existing user pool, filter out the selected user since it will
        // always match.
        return (
            existingUsers
                .filter((userEntry: DetailedUser) => userEntry.id !== user?.id)
                .filter((user: DetailedUser): boolean => user.email.trim().toLowerCase() === email.trim().toLowerCase()).length >
            0
        );
    }

    function getFormErrors(): string {
        if (firstName.trim() === "") {
            return "First Name is blank.";
        }
        if (lastName.trim() === "") {
            return "Last Name is blank.";
        }
        if (email.trim() === "") {
            return "Email is blank.";
        }
        if (emailTaken()) {
            return "Email in use.";
        }
        if (!validateEmail(email.trim())) {
            return "Email is not valid.";
        }

        return "";
    }

    function getFormWarnings(): string {
        if (getFormErrors() === "") {
            if (!email.endsWith("@techwiseis.com")) {
                return "Email is outside of the Techwise Domain.";
            }
        }
        return "";
    }

    function setDefaultRoleInfo() {
        if (!user) {
            setHasTech(false);
            setHasOffice(false);
            setHasAdmin(false);
            setHasMaterials(false);
            setHasProjects(false);
            setFirstName("");
            setLastName("");
            setEmail("");
            setEditingGeneralInfo(false);
            return;
        } else {
            let roles: string[] = [];

            // The backend sends the role ID as well as the name for this view.
            // This is a data adapter so that code in other areas doesn't need to be changed.
            type RoleDefinition = { name: string; id: number };
            (user?.roles as unknown as RoleDefinition[]).map((role: RoleDefinition) => {
                roles.push(role.name);
                return role.name;
            });

            setHasTech(roles.includes("technician"));
            setHasOffice(roles.includes("office"));
            setHasAdmin(roles.includes("admin"));
            setHasMaterials(roles.includes("materials"));
            setHasProjects(roles.includes("projects"));
        }
    }

    useEffect(() => {
        setDefaultUserGeneralInfo();
        setDefaultRoleInfo();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, dispatch]);

    if (!user) {
        return (
            <Card sx={{ ml: 5 }}>
                <CardContent>
                    <Typography textAlign="center">Please select a user to continue.</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ ml: 5 }}>
            <CardContent>
                <Typography variant="h5" sx={{ mb: 4 }}>{`@${user.username}`}</Typography>

                {!editingGeneralInfo && (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h6">{"Manage User's Information"}</Typography>
                        <Tooltip title="Edit User Information">
                            <IconButton onClick={() => setEditingGeneralInfo(true)}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
                {editingGeneralInfo && (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h6">Manage User's Information</Typography>
                        <Box>
                            <Tooltip title="Cancel Changes to User Information">
                                <IconButton
                                    onClick={() => {
                                        setDefaultUserGeneralInfo();
                                        setEditingGeneralInfo(false);
                                    }}
                                >
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Update Changes to User Information">
                                <span>
                                    <IconButton
                                        onClick={() => {
                                            updateUserInfo(
                                                user.id,
                                                {
                                                    first_name: firstName.trim(),
                                                    last_name: lastName.trim(),
                                                    email: email.trim(),
                                                },
                                                dispatch,
                                                () => {
                                                    refreshUsers(dispatch, (isLoading) => {});
                                                }
                                            );
                                            setEditingGeneralInfo(false);
                                        }}
                                        disabled={getFormErrors() !== ""}
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Stack>
                )}

                <Box sx={{ mb: 2 }} />

                {editingGeneralInfo && getFormErrors() !== "" && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {getFormErrors()}
                    </Alert>
                )}

                {editingGeneralInfo && getFormWarnings() !== "" && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {getFormWarnings()}
                    </Alert>
                )}

                {editingGeneralInfo && getFormErrors() === "" && getFormWarnings() === "" && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        No errors detected
                    </Alert>
                )}

                <UncontrolledEditableField
                    value={firstName}
                    setter={setFirstName}
                    label="First Name"
                    editing={editingGeneralInfo}
                />

                <UncontrolledEditableField value={lastName} setter={setLastName} label="Last Name" editing={editingGeneralInfo} />

                <UncontrolledEditableField
                    value={email}
                    setter={setEmail}
                    label="Email"
                    editing={editingGeneralInfo}
                    error={emailTaken() || !validateEmail(email)}
                />

                <Box sx={{ marginBottom: "20px" }} />

                <Typography variant="h6">Manage User's Roles</Typography>
                <FormGroup>
                    <UserRoleItemToggle value={hasTech} roleKey="technician" label="Technician" setter={setHasTech} user={user} />
                    <UserRoleItemToggle value={hasOffice} roleKey="office" label="Office" setter={setHasOffice} user={user} />
                    <UserRoleItemToggle
                        value={hasProjects}
                        roleKey="projects"
                        label="Project Management"
                        setter={setHasProjects}
                        user={user}
                    />
                    <UserRoleItemToggle
                        value={hasMaterials}
                        roleKey="materials"
                        label="Materials Database"
                        setter={setHasMaterials}
                        user={user}
                    />
                    <UserRoleItemToggle value={hasAdmin} roleKey="admin" label="Admin" setter={setHasAdmin} user={user} />
                </FormGroup>

                <Box sx={{ mb: 2 }} />

                <Typography variant="h6">Manage User's Access</Typography>

                <ConfirmDeleteModal
                    confirmText={`delete @${user.username}`}
                    description={`You are about to permanently delete the user @${user.username}. This also includes any assignment data they may have been responsible for. It is strongly advised that you disable a user instead. Exceptions include: 1.) Creating an account by mistake OR, 2.) Deleting a user who you no longer have a regulatory or contractual requirement to retain status.`}
                    open={deleteUserModalOpen}
                    handleClose={closeDeleteUserModal}
                    onDelete={(modalCallback: Effect) => {
                        deleteUser(user.id, dispatch, () => {
                            onRemoveUser(user.id);
                            modalCallback();
                        });
                    }}
                />
                <Box sx={{ mt: 2 }}>
                    {user.active && (
                        <Tooltip title="Disable User's Access to the System">
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() =>
                                    disableUser(user.id, dispatch, () => refreshUsers(dispatch, (isLoading: boolean) => {}))
                                }
                            >
                                Disable User
                            </Button>
                        </Tooltip>
                    )}
                    {!user.active && (
                        <Tooltip title="Enable User's Access to the System">
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => enableUser(user.id, dispatch, () => refreshUsers(dispatch, (isLoading) => {}))}
                            >
                                Enable User
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title="Permanently Remove the User from the System">
                        <Button onClick={() => setDeleteUserModalOpen(true)} variant="contained" color="error" sx={{ ml: 2 }}>
                            Delete User
                        </Button>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );
};

export default UserInfoPanel;
